<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue';

interface Props {
  phoneNumber: string;
  loading?: boolean;
  error?: string;
}

interface Emits {
  (e: 'verify', code: string): void;
  (e: 'resend'): void;
  (e: 'cancel'): void;
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

const code = ref(['', '', '', '', '', '']);
const inputRefs = ref<HTMLInputElement[]>([]);
const remainingTime = ref(600); // 10 minutes in seconds
let countdownInterval: number | null = null;

// Format phone number for display
const formattedPhone = computed(() => {
  const phone = props.phoneNumber;
  if (phone.startsWith('+1') && phone.length === 12) {
    // US format: +1 (XXX) XXX-XXXX
    return `+1 (${phone.slice(2, 5)}) ${phone.slice(5, 8)}-${phone.slice(8)}`;
  } else if (phone.length > 3) {
    // International: +XX XXX XXX...
    return `${phone.slice(0, 3)} ${phone.slice(3)}`;
  }
  return phone;
});

// Format remaining time as MM:SS
const formattedTime = computed(() => {
  const minutes = Math.floor(remainingTime.value / 60);
  const seconds = remainingTime.value % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
});

// Check if code is complete
const isCodeComplete = computed(() => {
  return code.value.every(digit => digit !== '');
});

// Get full code as string
const fullCode = computed(() => {
  return code.value.join('');
});

// Start countdown timer
const startCountdown = () => {
  if (countdownInterval) {
    clearInterval(countdownInterval);
  }
  
  remainingTime.value = 600; // Reset to 10 minutes
  
  countdownInterval = window.setInterval(() => {
    if (remainingTime.value > 0) {
      remainingTime.value--;
    } else {
      stopCountdown();
    }
  }, 1000);
};

const stopCountdown = () => {
  if (countdownInterval) {
    clearInterval(countdownInterval);
    countdownInterval = null;
  }
};

// Handle input in a code box
const handleInput = (index: number, event: Event) => {
  const target = event.target as HTMLInputElement;
  const value = target.value;
  
  // Only allow digits
  const digit = value.replace(/\D/g, '').slice(-1);
  code.value[index] = digit;
  
  // Move to next input if digit entered
  if (digit && index < 5) {
    inputRefs.value[index + 1]?.focus();
  }
  
  // Auto-verify when all 6 digits entered
  if (isCodeComplete.value) {
    emit('verify', fullCode.value);
  }
};

// Handle key down
const handleKeyDown = (index: number, event: KeyboardEvent) => {
  // Backspace
  if (event.key === 'Backspace') {
    if (code.value[index] === '' && index > 0) {
      // Move to previous input if current is empty
      inputRefs.value[index - 1]?.focus();
    } else {
      // Clear current input
      code.value[index] = '';
    }
  }
  
  // Arrow keys
  if (event.key === 'ArrowLeft' && index > 0) {
    inputRefs.value[index - 1]?.focus();
  }
  if (event.key === 'ArrowRight' && index < 5) {
    inputRefs.value[index + 1]?.focus();
  }
  
  // Enter key
  if (event.key === 'Enter' && isCodeComplete.value) {
    emit('verify', fullCode.value);
  }
};

// Handle paste
const handlePaste = (event: ClipboardEvent) => {
  event.preventDefault();
  const pastedData = event.clipboardData?.getData('text').replace(/\D/g, '') || '';
  
  if (pastedData.length === 6) {
    // Fill all inputs with pasted code
    code.value = pastedData.split('');
    inputRefs.value[5]?.focus();
    
    // Auto-verify
    emit('verify', pastedData);
  }
};

// Clear code
const clearCode = () => {
  code.value = ['', '', '', '', '', ''];
  inputRefs.value[0]?.focus();
};

// Handle resend
const handleResend = () => {
  clearCode();
  startCountdown();
  emit('resend');
};

// Lifecycle
onMounted(() => {
  startCountdown();
  inputRefs.value[0]?.focus();
});

onUnmounted(() => {
  stopCountdown();
});
</script>

<template>
  <div class="space-y-6">
    <!-- Header -->
    <div class="text-center">
      <div class="inline-flex items-center justify-center w-16 h-16 bg-primary-100 dark:bg-primary-900/30 rounded-full mb-4">
        <svg class="w-8 h-8 text-primary-600 dark:text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
        </svg>
      </div>
      
      <h3 class="text-xl font-semibold text-gray-900 dark:text-white mb-2">
        Verify Your Phone Number
      </h3>
      
      <p class="text-sm text-gray-600 dark:text-gray-400">
        We sent a 6-digit code to
      </p>
      <p class="text-sm font-medium text-gray-900 dark:text-white">
        {{ formattedPhone }}
      </p>
    </div>

    <!-- Code Input -->
    <div class="flex justify-center gap-2">
      <input
        v-for="(digit, index) in code"
        :key="index"
        :ref="el => inputRefs[index] = el as HTMLInputElement"
        type="text"
        inputmode="numeric"
        maxlength="1"
        :value="digit"
        @input="handleInput(index, $event)"
        @keydown="handleKeyDown(index, $event)"
        @paste="index === 0 ? handlePaste($event) : null"
        :disabled="loading"
        class="w-12 h-14 text-center text-2xl font-semibold bg-white dark:bg-gray-800 border-2 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all text-gray-900 dark:text-white"
        :class="error ? 'border-red-500 dark:border-red-500' : 'border-gray-300 dark:border-gray-600'"
      />
    </div>

    <!-- Error message -->
    <div v-if="error" class="text-center">
      <p class="text-sm text-red-600 dark:text-red-400 flex items-center justify-center gap-2">
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        {{ error }}
      </p>
    </div>

    <!-- Timer and Resend -->
    <div class="text-center space-y-3">
      <div v-if="remainingTime > 0" class="text-sm text-gray-600 dark:text-gray-400">
        Code expires in <span class="font-semibold text-gray-900 dark:text-white">{{ formattedTime }}</span>
      </div>
      
      <div v-else class="text-sm text-red-600 dark:text-red-400 font-medium">
        Code expired. Please request a new one.
      </div>

      <button
        @click="handleResend"
        :disabled="loading || remainingTime > 540"
        class="text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        <span v-if="remainingTime > 540">Resend available in {{ Math.ceil((remainingTime - 540) / 60) }}m</span>
        <span v-else>Didn't receive the code? Resend</span>
      </button>
    </div>

    <!-- Actions -->
    <div class="flex gap-3">
      <button
        @click="emit('cancel')"
        :disabled="loading"
        class="flex-1 px-4 py-2 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        Cancel
      </button>
      
      <button
        @click="emit('verify', fullCode)"
        :disabled="!isCodeComplete || loading"
        class="flex-1 px-4 py-2 text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
      >
        <svg v-if="loading" class="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        <span>{{ loading ? 'Verifying...' : 'Verify' }}</span>
      </button>
    </div>

    <!-- Help text -->
    <div class="text-center">
      <p class="text-xs text-gray-500 dark:text-gray-400">
        Enter the 6-digit code we sent to your phone
      </p>
    </div>
  </div>
</template>

<style scoped>
/* Remove default number input styles */
input[type='text']::-webkit-inner-spin-button,
input[type='text']::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

/* Smooth focus transition */
input:focus {
  transform: scale(1.05);
}
</style>
